/**
 * Partner Reports API
 * Handles API calls for report file upload and management
 */

import axiosInstance from '../axiosInstance';
import type {
  PartnerReport,
  ReportFilters,
} from './types';
import type {
  UploadReportRequest,
  ApiResponse,
  ApiListResponse,
  UploadProgress,
} from '@/types/partnerReports';

const BASE_URL = '/api/partner-reports';

/**
 * Get all reports with optional filters
 */
export const getReports = async (filters?: ReportFilters): Promise<{
  data: PartnerReport[];
  total: number;
  page: number;
  pages: number;
}> => {
  const response = await axiosInstance.get<{
    data: PartnerReport[];
    total: number;
    page: number;
    pages: number;
  }>(
    BASE_URL,
    { params: filters }
  );
  return response.data;
};

/**
 * Get reports for a specific partner
 */
export const getPartnerReports = async (
  partnerId: string,
  filters?: {
    reportType?: string;
    status?: string;
    page?: number;
    limit?: number;
  }
): Promise<{
  data: PartnerReport[];
  total: number;
  page: number;
  pages: number;
}> => {
  const response = await axiosInstance.get<{
    data: PartnerReport[];
    total: number;
    page: number;
    pages: number;
  }>(
    BASE_URL,
    {
      params: { partnerId, ...filters },
    }
  );
  return response.data;
};

/**
 * Get a specific report by ID
 */
export const getReport = async (id: string): Promise<PartnerReport> => {
  const response = await axiosInstance.get<{ data: PartnerReport }>(
    `${BASE_URL}/${id}`
  );
  return response.data.data;
};

/**
 * Upload a report file
 */
export const uploadReport = async (
  request: UploadReportRequest,
  onProgress?: (progress: UploadProgress) => void
): Promise<PartnerReport> => {
  const formData = new FormData();
  formData.append('file', request.file);
  formData.append('fieldMappingId', request.reportTypeId); // Backend expects fieldMappingId
  formData.append('partnerId', request.partnerId);
  formData.append('quarter', request.quarter.toString());
  formData.append('year', request.year.toString());

  if (request.title) {
    formData.append('title', request.title);
  }
  if (request.description) {
    formData.append('description', request.description);
  }

  const response = await axiosInstance.post<{ data: PartnerReport }>(
    `${BASE_URL}/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress: UploadProgress = {
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
          };
          onProgress(progress);
        }
      },
    }
  );

  return response.data.data;
};

/**
 * Create a report entry (without file upload)
 */
export const createReport = async (data: {
  partnerId?: string;
  title: string;
  description?: string;
  reportType: string;
  metadata?: any;
}): Promise<PartnerReport> => {
  const response = await axiosInstance.post<{ data: PartnerReport }>(
    BASE_URL,
    data
  );
  return response.data.data;
};

/**
 * Update a report
 */
export const updateReport = async (
  id: string,
  data: Partial<PartnerReport>
): Promise<PartnerReport> => {
  const response = await axiosInstance.put<{ data: PartnerReport }>(
    `${BASE_URL}/${id}`,
    data
  );
  return response.data.data;
};

/**
 * Delete a report
 */
export const deleteReport = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/${id}`);
};

/**
 * Process a report
 */
export const processReport = async (id: string): Promise<PartnerReport> => {
  const response = await axiosInstance.post<{ data: PartnerReport }>(
    `${BASE_URL}/${id}/process`
  );
  return response.data.data;
};

/**
 * Get report processing status
 */
export const getReportStatus = async (id: string): Promise<{
  status: string;
  progress: number;
  message: string;
}> => {
  const response = await axiosInstance.get(`${BASE_URL}/${id}/status`);
  return response.data;
};

/**
 * Download report file
 */
export const downloadReport = async (id: string): Promise<Blob> => {
  const response = await axiosInstance.get(`${BASE_URL}/${id}/download`, {
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Download report processing results
 */
export const downloadReportResults = async (id: string): Promise<Blob> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/${id}/results/download`,
    {
      responseType: 'blob',
    }
  );
  return response.data;
};

/**
 * Retry failed report processing
 */
export const retryReport = async (id: string): Promise<PartnerReport> => {
  const response = await axiosInstance.post<{ data: PartnerReport }>(
    `${BASE_URL}/${id}/retry`
  );
  return response.data.data;
};

/**
 * Bulk delete reports
 */
export const bulkDeleteReports = async (reportIds: string[]): Promise<{
  deleted: number;
  message: string;
}> => {
  const response = await axiosInstance.post(`${BASE_URL}/bulk-delete`, {
    reportIds,
  });
  return response.data;
};

/**
 * Get report analytics
 */
export const getReportAnalytics = async (partnerId?: string): Promise<any> => {
  const response = await axiosInstance.get(`${BASE_URL}/analytics`, {
    params: partnerId ? { partnerId } : undefined,
  });
  return response.data;
};

/**
 * Get report templates
 */
export const getReportTemplates = async (): Promise<any[]> => {
  const response = await axiosInstance.get(`${BASE_URL}/templates`);
  return response.data.data;
};
